<template>
  <div>
    <apexchart :options="chartOptions" :series="chartSeries" height="400" type="line"></apexchart>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import api from "../../services/api";
import frLocale from 'apexcharts/dist/locales/fr.json'

export default {
  name: 'TreasuryChart',

  components: {
    apexchart: VueApexCharts,
  },

  props: {
    cardValues: {
      type: Array
    },
    isGlobal:{
      required:false,
      type:Boolean,
      default:false,
    },
    operationId: {
      default: null
    },
    startDate: {
      default: 0,
      required: false
    },
    endDate: {
      default: 0,
      required: false
    }
  },

  data() {
    return {
      operationName:"",
      societeMovements: [],
      investorMovements: [],
      totalMovements: [],
      emprunts: [],
      chartOptions: {
        annotations: {
          xaxis: [
            {
              x: new Date().getTime(),
              borderColor: '#000000',
              label: {
                borderColor: '#B0BFE8',
                orientation: 'horizontal',
                position: 'top',
                style: {
                  background: "#B0BFE8",
                  color: "#07153C",
                  fontWeight: 'bold',
                  fontSize: '12px'
                },
                text: new Date().toLocaleDateString()
              }
            }
          ]
        },
        chart: {
          id: 'treasury-chart',
          locales: [frLocale],
          defaultLocale: 'fr',
          toolbar: {
            show: true,
            tools: {
              download: true, // Affiche le bouton de téléchargement
              selection: false, // Affiche le bouton de sélection
              zoom: false, // Affiche le bouton de zoom
              zoomin: true, // Affiche le bouton de zoom in
              zoomout: true, // Affiche le bouton de zoom out
              pan: true, // Affiche le bouton de pan
              reset: true, // Affiche le bouton de reset (home)
            },
          },
        },
        stroke: {
          curve: 'stepline'
        },
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false
          },
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          title: {
            text: this.$t('treasury.chart.yaxis.label'),
          },
        },
        markers: {
          size: 4,
          strokeWidth: 0,
          hover: {
            sizeOffset: 1,
          },
        },
        tooltip: {
          x: {
            show: true,
            format: "dd MMM yyyy",
            formatter: function (value, {series, seriesIndex, dataPointIndex, w}) {
              return new Date(value).toLocaleDateString('fr-fr', {
                weekday: "long",
                year: "numeric",
                month: "short",
                day: "numeric"
              })
            },
          },
          shared: true,
          custom: this.customSharedTooltip
        },

        colors: ['#6D84C4', '#06C9B3', '#9E0430']
      },
      chartSeries: [
        {
          name: this.$t('treasury.chart.societe'),
          data: [],
        }
      ],
    };
  },

  async mounted() {
    if(this.operationId){
      await this.fetchOperation()
    }
    await this.fetchTreasuryMovements()
    await this.updateChartSeries();
    this.updateColorSeries();
  },

  watch: {
    chartSeries: {
      handler: function (newVal) {
        this.updateColorSeries();
      },
      deep: true,
    },
  },
  methods: {

    async fetchOperation() {
      await api
        .get(`/operation/${this.operationId}`)
        .then(response => {
          this.operationName = response.data.name
        })
    },

    async fetchTreasuryMovements() {
      console.log("fetchTreasuryMovements")
      try {
        let url = "/treasury/getAllTreasuryMovementAndEvenement"
        if(this.isGlobal && this.operationId){
          url = `/treasury/getAllTreasuryMovementAndEvenementForOperation/${this.operationId}`
        }
        const response = await api.get(url, {
          params: {startDate: this.startDate, endDate: this.endDate},
        });

        let baseMovements = []

        baseMovements.push({data: this.formatDate(response.data.societe)})

        if(response.data.investor.length > 0){
          baseMovements.push({data: this.formatDate(response.data.investor)})
          this.chartSeries.push({
            name: this.$t('treasury.chart.investor'),
            data: [],
          })
        }
        if(response.data.emprunts.length > 0){
          baseMovements.push({data: this.formatDate(response.data.emprunts)})
          this.chartSeries.push({
            name: this.$t('treasury.chart.emprunts'),
            data: [],
          })
        }


        baseMovements.forEach((movement, i) => {
          this.chartSeries[i].data = this.formatDataForChart(movement.data);
        });

        // Mise à jour des valeurs des cards
        let today = new Date()
        this.chartSeries.forEach((courbe, i) => {
          if(courbe && courbe.data){
            courbe.data.forEach(xy => {
              if (new Date(xy[0]) < today) {
                this.cardValues.splice(i, 1, xy[1])
              }
            })
          }
        })

      } catch (error) {
        console.error(error);
      }
    },

    formatDataForChart(treasuryMovements) {
      if (!treasuryMovements) return
      return treasuryMovements.map((movement) => {
        if(!movement) return [null, null, null]
        if(this.operationId && movement && movement.expense){
          movement.expense = movement.expense.map((expense) => {
            return expense ? {
                ...expense,
                name: expense.name.replaceAll(`- ${this.operationName}`, '')
              } : null
            }
          )
        }
        const date = new Date(movement.date);
        return [date.getTime(), Math.trunc(movement.solde), movement.expense];
      });
    },

    formatDate(object) {
      if (!object || object.length === 0) return
      return object.map(movement => {
        if (!movement || !movement.date) return
        const date = new Date(movement.date);
        return {...movement, date: date, expenses: [movement.expense]};
      })
    },

    updateChartSeries() {
      this.chartSeries = [...this.chartSeries];
    },

    updateColorSeries() {
      switch (this.chartSeries.length) {
        case 1:
          this.chartOptions.colors = ['#6D84C4'];
          break;
        case 2:
          if (this.chartSeries[1].name === this.$t('treasury.chart.investor')) {
            this.chartOptions.colors = ['#6D84C4', '#06C9B3'];
          } else {
            this.chartOptions.colors = ['#6D84C4', '#9E0430'];
          }
          break;
        case 3:
          this.chartOptions.colors = ['#6D84C4', '#06C9B3', '#9E0430'];
          break;
        default:
          this.chartOptions.colors = ['#6D84C4', '#06C9B3', '#9E0430'];
          break;
      }
      // Forcer la réactivité en créant une nouvelle instance de chartOptions
      this.chartOptions = { ...this.chartOptions };
    },


    customSharedTooltip({series, seriesIndex, dataPointIndex, w}) {
      try {

        if (!w || !w.globals || !w.globals.seriesX || !w.globals.seriesX[seriesIndex]) {
          return "";
        }

        const hoverXaxis = w.globals.seriesX[seriesIndex][dataPointIndex];
        if (!hoverXaxis) {
          return "";
        }

        const hoverDate = new Date(hoverXaxis);
        hoverDate.setHours(0, 0, 0, 0);

        const hoverIndexes = w.globals.seriesX.map((seriesX) => {
          if (!seriesX) return -1;

          return seriesX.findIndex((xData) => {
            if (!xData) return false;

            const xDate = new Date(xData);
            xDate.setHours(0, 0, 0, 0);

            return Math.abs(xDate.getTime() - hoverDate.getTime()) < 86400000; // Tolérance d'un jour
          });
        });

        let hoverList = "";

        hoverIndexes.forEach((hoverIndex, seriesEachIndex) => {

          if (hoverIndex >= 0 &&
              series &&
              series[seriesEachIndex] &&
              series[seriesEachIndex][hoverIndex] !== undefined) {

            const currentData = this.chartSeries[seriesEachIndex].data;
            let expense = "";


            if (currentData && currentData[hoverIndex]) {
              const expenseData = currentData[hoverIndex][2];


              if (expenseData) {
                if (Array.isArray(expenseData)) {
                  expenseData.forEach(obj => {
                    if (obj && typeof obj === 'object' && obj.name) {
                      expense += `<div>${obj.name}:<span><strong>${obj.type === "sortant" ? ' ' : ' +'}${Intl.NumberFormat().format((obj.value || 0)) + '€'}</strong></span></div>`;
                    }
                  });
                } else if (typeof expenseData === 'object' && expenseData.name) {
                  // Cas où expenseData est un objet unique
                  expense += `<div>${expenseData.name}:<span><strong>${expenseData.type === "sortant" ? ' ' : ' +'}${Intl.NumberFormat().format((expenseData.value || 0)) + '€'}</strong></span></div>`;
                }
              }
            }

            // Vérifier si les couleurs des marqueurs sont disponibles
            const markerColor = w.globals.markers && w.globals.markers.colors && w.globals.markers.colors[seriesEachIndex]
              ? w.globals.markers.colors[seriesEachIndex]
              : '#000000';

            // Vérifier si les noms de séries sont disponibles
            const seriesName = w.globals.seriesNames && w.globals.seriesNames[seriesEachIndex]
              ? w.globals.seriesNames[seriesEachIndex]
              : `Série ${seriesEachIndex + 1}`;

            // Formatage de la valeur
            let formattedValue = '';
            try {
              if (w.globals.yLabelFormatters && w.globals.yLabelFormatters[0]) {
                formattedValue = Intl.NumberFormat().format(w.globals.yLabelFormatters[0](series[seriesEachIndex][hoverIndex])) + '€';
              } else {
                formattedValue = Intl.NumberFormat().format(series[seriesEachIndex][hoverIndex]) + '€';
              }
            } catch (e) {
              formattedValue = '0 €';
            }

            // Construction du HTML pour cette série
            hoverList += `
              <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex; min-width: 300px;">
                  <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 13px;min-width: 300px; border-left: 3px solid ${markerColor}; padding-left: 6px;">
                      <div class="apexcharts-tooltip-y-group" style="display: flex; width: 100%; justify-content: space-between;">
                          <span class="apexcharts-tooltip-text-y-label">
                              <b>${seriesName}:</b>
                          </span>
                          <span class="apexcharts-tooltip-text-y-value">
                              ${formattedValue}
                          </span>
                      </div>
                      <div class="additional-content" style="margin-bottom: 0.5em;font-size: 12px;">
                            ${expense}
                      </div>
                  </div>
              </div>`;
          }
        });

        // Formatage de la date pour le titre
        let parsed = '';
        try {
          parsed = new Date(hoverXaxis).toLocaleDateString('fr-fr', {
            weekday: "long",
            year: "numeric",
            month: "short",
            day: "numeric"
          });
        } catch (e) {
          parsed = 'Date inconnue';
        }

        // Retourner le tooltip complet
        if (hoverXaxis) {
          return `<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">${
            parsed
          }</div>${hoverList}`;
        }

        return "";
      } catch (error) {
        console.error("Erreur dans customSharedTooltip:", error);
        return "";
      }
    },
  },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/views/treasury/TreasuryChart.vue